import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {DEBOUNCE_TIME, extractErrorMessage} from 'src/app/common/common';
import {HistoryRegisteredReport} from 'src/app/model/registeredReports.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: 'app-report-history-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, NgxPaginationModule, LoaderComponent],
  templateUrl: './report-history-index.component.html',
  styleUrl: './report-history-index.component.scss'
})
export class ReportHistoryIndexComponent implements OnInit, OnDestroy {
  protected readonly Boolean = Boolean;
  // Search fields
  public requestIdSearch: string = '';
  public registeredReportIdSearch: string = '';
  public dateSearch: string = '';
  public intervalsSentSearch: string = '';
  public receivedReplySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    requestId: '',
    registeredReportId: '',
    date: '',
    receivedReply: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'reportHistoryPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public registered_report_id: string = '';
  public router: Router;
  public isLoading: boolean = false;
  public error: string | null = null;
  public displayedReportHistory: HistoryRegisteredReport[] = [];

  constructor(private injector: Injector, private reportHistoryService: ApiService) {
    this.router = injector.get(Router);

    // Get registered report ID from route params
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.registered_report_id = event.snapshot.params.id;
        this.registeredReportIdSearch = this.registered_report_id; // Pre-populate search
      }
    });
  }

  ngOnInit(): void {
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadReportHistory();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 1,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
      'sort': 'dttm', // Sort by date as in original
    };

    // Add search parameters if they exist
    if (this.requestIdSearch) {
      params['request_id'] = this.requestIdSearch.trim();
    }
    if (this.registeredReportIdSearch) {
      params['registered_report_id'] = this.registeredReportIdSearch.trim();
    }
    if (this.dateSearch) {
      params['dttm'] = this.dateSearch.trim();
    }
    if (this.receivedReplySearch) {
      // Convert to boolean for API
      const boolValue = this.receivedReplySearch.toLowerCase() === 'true';
      params['success'] = boolValue.toString();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      requestId: this.requestIdSearch,
      registeredReportId: this.registeredReportIdSearch,
      date: this.dateSearch,
      intervalsSent: this.intervalsSentSearch,
      receivedReply: this.receivedReplySearch
    });
  }

  loadReportHistory() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    // Use the new API pattern
    this.reportHistoryService.getReportHistory(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedReportHistory = response as HistoryRegisteredReport[];
          this.paginationModel = this.pagination.updateTotalItems(response.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading report history:', err);
          this.error = extractErrorMessage(err, "Failed to load report history.");
          this.isLoading = false;
        }
      });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadReportHistory();
  }
}
